// "The Filing" — sign-in reframed as an official compliance document.
// Pixel-port of the Claude Design handoff (carbon-clear/project/login-filing.jsx,
// the explicitly-final login direction "D · The Filing"). Document masthead
// ruled mono fields, serif headline, embossed teal
// VERIFIED seal. Invite-only: no self-registration — footer routes to
// info@codeclear.com (per the design's intent, confirmed with Leo 2026-06-04).
//
// The design prototype was a static mock with hardcoded values and a redirect
// to Loading.html. This component keeps the design's exact visual structure but
// wires the REAL auth flow from LoginForm.tsx: login() -> refetch /me -> route
// to the user's platform home (or the ?redirect target).
//
// Colors bridge the existing token system (index.css :root) per
// feedback_tailwind_tokens_via_cssvars_only — --teal / --ink / --ink-mute /
// --ink-line already match the design's palette; --login-paper / --login-faint
// were added for the two values the design uses that weren't yet tokenized.
import React, { useState } from "react";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";

import { TOO_MANY_ATTEMPTS, isThrottled, login } from "@/api/auth";
import { useMeQuery } from "@/queries/queries";
import { postLoginTarget } from "@/lib/postLoginHome";

const routeApi = getRouteApi("/login");

// Palette — bridged to CSS vars, not hand-rolled hex (see header note).
const INK = "hsl(var(--ink))";
const MUTE = "hsl(var(--ink-mute))";
const FAINT = "hsl(var(--login-faint))";
const LINE = "hsl(var(--ink-line))";
const PAPER = "hsl(var(--login-paper))";
const TEAL = "hsl(var(--teal))";
const DANGER = "hsl(var(--destructive))";

const SERIF = "'Newsreader', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";

function FilingLogo({ color = INK }: { color?: string }) {
  // Official CodeClear cube mark (filled green top face + ink strokes) —
  // matches /codeClearLogo.svg and the landing-page mark exactly so the
  // login header reads the same brand as the rest of the app. The
  // wordmark is "CodeClear" (one word, title-case), not "code clear".
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width="24" height="24" viewBox="0 0 32 32" fill="none" aria-hidden="true">
        <path d="M16 4 L27 10.5 L16 17 L5 10.5 Z" fill="#1A8A60" />
        <path d="M16 4 L27 10.5 L27 22 L16 28.5 L5 22 L5 10.5 Z" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M5 10.5 L16 17 L27 10.5 M16 17 L16 28.5" fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M16 20.8 L27 14.3 M16 24.7 L27 18.2 M5 14.3 L16 20.8 M5 18.2 L16 24.7" fill="none" stroke={color} strokeWidth="1.1" strokeLinecap="round" opacity="0.5" />
      </svg>
      <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.4, color }}>
        CodeClear
      </span>
    </div>
  );
}

type FilingFieldProps = {
  label: string;
  name: string;
  type?: "text" | "email" | "password";
  last?: boolean;
  autoFocus?: boolean;
  onCapsLock?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

function FilingField({ label, name, type = "text", last, autoFocus, onCapsLock }: FilingFieldProps) {
  const [show, setShow] = useState(false);
  const [focus, setFocus] = useState(false);
  const isPw = type === "password";
  const inputType = isPw ? (show ? "text" : "password") : type;
  return (
    <div
      style={{
        borderBottom: last ? "none" : `1px solid ${LINE}`,
        padding: "20px 0",
        display: "grid",
        gridTemplateColumns: "120px 1fr auto",
        alignItems: "center",
        gap: 16,
      }}
    >
      <label
        htmlFor={name}
        style={{
          fontFamily: MONO,
          fontSize: 10,
          letterSpacing: 1.2,
          textTransform: "uppercase",
          color: focus ? TEAL : MUTE,
          transition: "color 140ms",
        }}
      >
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={inputType}
        required
        autoFocus={autoFocus}
        autoComplete={isPw ? "current-password" : "email"}
        inputMode={isPw ? undefined : "email"}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        onKeyDown={onCapsLock}
        onKeyUp={onCapsLock}
        style={{
          all: "unset",
          width: "100%",
          fontSize: 17,
          fontFamily: MONO,
          color: INK,
          letterSpacing: isPw && !show ? 2 : 0,
          borderBottom: `1.5px solid ${focus ? TEAL : "transparent"}`,
          paddingBottom: 3,
          transition: "border-color 140ms",
        }}
      />
      {isPw && (
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-pressed={show}
          aria-label={show ? "Hide password" : "Show password"}
          style={{
            all: "unset",
            cursor: "pointer",
            color: MUTE,
            fontFamily: MONO,
            fontSize: 10,
            letterSpacing: 1,
            textTransform: "uppercase",
            padding: "4px 0",
          }}
        >
          {show ? "Hide" : "Show"}
        </button>
      )}
    </div>
  );
}

const LoginFiling = () => {
  const navigate = useNavigate();
  const search = routeApi.useSearch() as { redirect?: string };
  const meQuery = useMeQuery();

  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);

  const handleCapsLock = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setCapsLockOn(e.getModifierState("CapsLock"));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    setSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const email = String(formData.get("email") || "");
    const password = String(formData.get("password") || "");

    try {
      await login(email, password);
      // Refetch /me with the new token, then route to the user's platform
      // home. Tracker-home companies route through the /welcome warm-up splash;
      // Classic stays on /buildings. Centralized in postLoginTarget so all
      // three login entry points stay in lockstep (no splash flicker / race).
      const fresh = await meQuery.refetch();
      navigate(postLoginTarget(fresh.data, search?.redirect));
    } catch (err) {
      setErrorMessage(
        isThrottled(err)
          ? TOO_MANY_ATTEMPTS
          : "That email and password don't match. Try again or reset your password.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: PAPER,
        fontFamily: SANS,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 48,
      }}
    >
      <div style={{ width: 560, maxWidth: "100%" }}>
        {/* The document */}
        <div
          style={{
            background: "white",
            boxShadow:
              "0 1px 0 rgba(0,0,0,0.04), 0 40px 80px -30px rgba(20,20,20,0.18)",
          }}
        >
          {/* masthead */}
          <div
            style={{
              borderBottom: `2px solid ${INK}`,
              padding: "28px 48px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <FilingLogo />
          </div>

          <div style={{ padding: "32px 48px 0" }}>
            <div
              style={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: TEAL,
                marginBottom: 12,
              }}
            >
              Account holder sign-in
            </div>
            <h1
              style={{
                fontFamily: SERIF,
                fontWeight: 400,
                fontSize: 36,
                letterSpacing: -0.8,
                margin: "0 0 6px",
                lineHeight: 1.1,
                color: INK,
              }}
            >
              Authenticate to continue
            </h1>
            <p style={{ fontSize: 13, color: MUTE, margin: "0 0 24px", lineHeight: 1.6 }}>
              Verify your identity to access filings for your portfolio.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ borderTop: `1px solid ${LINE}` }}>
                <FilingField label="Email" name="email" type="email" autoFocus />
                <FilingField
                  label="Password"
                  name="password"
                  type="password"
                  onCapsLock={handleCapsLock}
                  last
                />
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: 14,
                  minHeight: 18,
                }}
              >
                <span style={{ fontSize: 12, color: MUTE }}>
                  {capsLockOn ? "Caps Lock is on" : ""}
                </span>
                <Link
                  to="/external/password_reset_request"
                  style={{ fontSize: 12, color: MUTE, textDecoration: "none" }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* error slot — kept in the document aesthetic */}
              <div style={{ minHeight: errorMessage ? undefined : 0, marginTop: errorMessage ? 14 : 0 }}>
                {errorMessage && (
                  <div
                    role="alert"
                    aria-live="polite"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      fontSize: 12.5,
                      color: DANGER,
                    }}
                  >
                    <AlertCircle style={{ width: 15, height: 15, flexShrink: 0 }} aria-hidden="true" />
                    {errorMessage === TOO_MANY_ATTEMPTS ? (
                      <span>{TOO_MANY_ATTEMPTS}</span>
                    ) : (
                      <span>
                        That email and password don't match. Try again or{" "}
                        <Link
                          to="/external/password_reset_request"
                          style={{ color: DANGER, textDecoration: "underline" }}
                        >
                          reset your password
                        </Link>
                        .
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* sign-in + seal */}
              <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 30 }}>
                <button
                  type="submit"
                  disabled={submitting}
                  aria-busy={submitting}
                  style={{
                    all: "unset",
                    flex: 1,
                    textAlign: "center",
                    height: 54,
                    background: INK,
                    color: "white",
                    fontSize: 15,
                    fontWeight: 600,
                    cursor: submitting ? "default" : "pointer",
                    opacity: submitting ? 0.85 : 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    transition: "transform 80ms, opacity 140ms",
                  }}
                  onMouseDown={(e) => {
                    if (!submitting) (e.currentTarget as HTMLButtonElement).style.transform = "translateY(1px)";
                  }}
                  onMouseUp={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = "none";
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.transform = "none";
                  }}
                >
                  {submitting ? "Signing in…" : "Sign in"}
                  {!submitting && <span aria-hidden="true">→</span>}
                </button>
                {/* embossed seal */}
                <div
                  aria-hidden="true"
                  style={{
                    width: 66,
                    height: 66,
                    borderRadius: 999,
                    border: `2px solid ${TEAL}`,
                    color: TEAL,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                    position: "relative",
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                    <path d="M16 4 L27 10.5 L16 17 L5 10.5 Z" fill={TEAL} opacity="0.18" />
                    <path d="M16 4 L27 10.5 L27 22 L16 28.5 L5 22 L5 10.5 Z" fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                    <path d="M5 10.5 L16 17 L27 10.5 M16 17 L16 28.5" fill="none" stroke={TEAL} strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
                  </svg>
                  <div style={{ fontFamily: MONO, fontSize: 6, letterSpacing: 1, marginTop: 3 }}>
                    VERIFIED
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* footer rule — invite-only, contact instead of register */}
          <div
            style={{
              margin: "32px 48px 0",
              padding: "18px 0 36px",
              borderTop: `1px solid ${LINE}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: 12,
              color: MUTE,
            }}
          >
            <span>
              CodeClear is invite-only.{" "}
              <a
                href="mailto:info@codeclear.com?subject=CodeClear%20access%20request"
                style={{ color: INK, textDecoration: "underline", fontWeight: 500 }}
              >
                info@codeclear.com
              </a>
            </span>
            <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: 1, textTransform: "uppercase" }}>
              Formerly Carbon Clear
            </span>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LoginFiling;
